import type { ZemChargeFailureStage } from './events.web-zem-charge';
import type { ZemChargePopupContext } from './zemChargePopupContext';

export const buildZemChargeContextProps = (context: ZemChargePopupContext) => ({
  charge_flow_id: context.chargeFlowId,
  ...(context.commerceFlowId && {
    commerce_flow_id: context.commerceFlowId,
  }),
  ...(context.place && { place: context.place }),
  ...(context.chargeReason && { charge_reason: context.chargeReason }),
  ...(context.requiredZem !== undefined && {
    required_zem: context.requiredZem,
  }),
  ...(context.balanceZem !== undefined && {
    balance_zem: context.balanceZem,
  }),
  ...(context.shortfallZem !== undefined && {
    shortfall_zem: context.shortfallZem,
  }),
});

export const buildZemChargeCompleteProps = ({
  context,
  currency,
  legacyAmount,
  legacyRevenue,
  method,
  paymentAmount,
  bonusZem,
  totalZem,
  promotionType,
}: {
  context: ZemChargePopupContext;
  currency: string;
  legacyAmount: number | string;
  legacyRevenue: number;
  method: string;
  paymentAmount: number;
  bonusZem: number;
  totalZem: number;
  promotionType: string;
}) => ({
  amount: legacyAmount,
  zem_amount: Number(legacyAmount),
  bonus_zem: bonusZem,
  total_zem: totalZem,
  promotion_type: promotionType,
  payment_amount: paymentAmount,
  currency,
  method,
  $revenue: legacyRevenue,
  ...buildZemChargeContextProps(context),
});

const ZEM_CHARGE_CANCEL_CODES = new Set(['PAY_PROCESS_CANCELED', 'C0998']);

const ZEM_CHARGE_ERROR_CODE_PATTERN = /^(?=.*[A-Za-z_])[A-Za-z0-9_-]{1,64}$/;

export const normalizeZemChargeErrorCode = (code?: string): string => {
  const normalizedCode = code?.trim();
  return normalizedCode && ZEM_CHARGE_ERROR_CODE_PATTERN.test(normalizedCode)
    ? normalizedCode
    : 'unknown';
};

export const getZemChargeErrorCode = (
  error: unknown,
  fallbackCode: string,
): string => {
  if (
    typeof error === 'object' &&
    error !== null &&
    'code' in error &&
    typeof (error as { code: unknown }).code === 'string'
  ) {
    return normalizeZemChargeErrorCode((error as { code: string }).code);
  }

  return fallbackCode;
};

export const getZemChargeFailureEventName = (code?: string) =>
  ZEM_CHARGE_CANCEL_CODES.has(normalizeZemChargeErrorCode(code))
    ? ('web_zem_charge_cancel' as const)
    : ('web_zem_charge_error' as const);

export const buildZemChargeFailureProps = ({
  code,
  context,
  stage,
}: {
  code?: string;
  context: ZemChargePopupContext;
  message?: string;
  stage: ZemChargeFailureStage;
}) => ({
  code: normalizeZemChargeErrorCode(code),
  stage,
  ...buildZemChargeContextProps(context),
});
